/*
 * Esri bills the basemap services either per request or per session: a session
 * is started once with the API key and then serves the whole base map — the
 * style document, the vector tiles and the raster imagery alike — for up to
 * twelve hours. This module owns that session so a page load starts exactly one,
 * shared by every map on screen.
 *
 * There is deliberately no fall back to the API key. Falling back would keep the
 * map alive by quietly moving it onto per-request billing, which is the one
 * outcome that cannot be noticed from the screen; failing outright is what makes
 * a broken session visible.
 */

const SESSIONS_URL =
  'https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/sessions/start'

/*
 * The style family the session covers. Sessions are scoped to one, and every
 * style this app uses ('arcgis/imagery/labels') belongs to this one.
 */
const STYLE_FAMILY = 'arcgis'

/*
 * Renew this far ahead of the stated expiry. Tiles already in flight when a
 * session turns over still carry the previous token, so the old one has to stay
 * valid for a little longer than the moment we stop handing it out.
 */
const RENEW_MARGIN_MS = 5 * 60 * 1000

export type ArcGISSession = {
  token: string
  /** Epoch milliseconds, straight from the service's `endTime`. */
  expiresAt: number
}

let current: ArcGISSession | null = null
let inFlight: Promise<ArcGISSession> | null = null

const isUsable = (session: ArcGISSession | null): session is ArcGISSession =>
  session !== null && Date.now() < session.expiresAt - RENEW_MARGIN_MS

const startSession = async (): Promise<ArcGISSession> => {
  if (!ARCGIS_API_KEY) {
    throw new Error(
      'ARCGIS_API_KEY is not set, so no basemap session can be started. Set it in packages/web-app/.env.',
    )
  }

  const url = `${SESSIONS_URL}?styleFamily=${STYLE_FAMILY}&token=${ARCGIS_API_KEY}&f=json`
  const response = await fetch(url)

  let body
  try {
    body = await response.json()
  } catch {
    throw new Error(
      `ArcGIS basemap session request returned ${response.status} and no JSON body`,
    )
  }

  // The service answers 200 with an error object rather than an HTTP status, so
  // the body is what decides whether this worked.
  if (body?.error) {
    throw new Error(
      `ArcGIS refused a basemap session: ${body.error.message ?? 'unknown error'}`,
    )
  }
  if (!body?.sessionToken || !body?.endTime) {
    throw new Error('ArcGIS basemap session response was missing a token')
  }

  return { token: body.sessionToken, expiresAt: body.endTime }
}

/**
 * The current basemap session, starting one if there is none or the existing one
 * is close enough to expiry to be worth replacing. Concurrent callers — the two
 * halves of the base map, or both panes of the split view — share a single
 * request rather than each starting a session of their own.
 *
 * Rejects if a session cannot be started. There is no API-key fallback.
 */
export const getArcGISSession = (): Promise<ArcGISSession> => {
  if (isUsable(current)) return Promise.resolve(current)

  if (!inFlight) {
    inFlight = startSession()
      .then((session) => {
        current = session
        return session
      })
      .finally(() => {
        inFlight = null
      })
  }

  return inFlight
}

/**
 * How long until the session should be renewed, in milliseconds, floored at zero.
 * Callers schedule their own renewal from this.
 */
export const msUntilRenewal = (session: ArcGISSession): number =>
  Math.max(0, session.expiresAt - RENEW_MARGIN_MS - Date.now())

/** Test seam: drops the cached session so the next call starts a fresh one. */
export const resetArcGISSession = () => {
  current = null
  inFlight = null
}

/*
 * Backoff for a failed start. Short, because the base map is the whole visual
 * context of a yield map and there is nothing to fall back to; bounded, because
 * the failures that are not transient — a revoked key, a referrer mismatch —
 * will never succeed and should not be retried forever.
 */
const RETRY_DELAYS_MS = [1_000, 4_000, 15_000]

/**
 * Keeps `apply` supplied with a live basemap session: applies one now, renews
 * before it lapses, and retries a failure on a bounded backoff. Once that budget
 * is spent it waits for the browser to come back online, which is the failure
 * this app actually sees — tablets in the field losing connectivity for longer
 * than a handful of retries covers.
 *
 * Returns a stop function; nothing is applied after it is called.
 */
export const keepArcGISSessionApplied = (
  apply: (token: string) => Promise<void>,
): (() => void) => {
  let stopped = false
  let timer: ReturnType<typeof setTimeout> | undefined
  let attempt = 0
  let failing = false

  const applyNow = () => {
    void (async () => {
      try {
        const session = await getArcGISSession()
        if (stopped) return
        await apply(session.token)
        if (stopped) return

        // A renewal half a day from now gets its own budget rather than
        // inheriting whatever this start happened to spend.
        attempt = 0
        failing = false
        timer = setTimeout(applyNow, msUntilRenewal(session))
      } catch (error) {
        if (stopped) return
        failing = true
        // Covers both halves: the session itself, and applying it to the
        // layers. A labels style that will not load is retried the same way, so
        // a transient outage does not leave the map without roads until the
        // next renewal half a day later.
        console.error('ArcGIS base map could not be applied', error)

        const delay = RETRY_DELAYS_MS[attempt]
        if (delay === undefined) return
        attempt += 1
        timer = setTimeout(applyNow, delay)
      }
    })()
  }

  // Only worth acting on while actually broken: an `online` event with a healthy
  // session would re-fetch the style for nothing.
  const retryWhenOnline = () => {
    if (stopped || !failing) return
    attempt = 0
    clearTimeout(timer)
    applyNow()
  }

  applyNow()
  if (typeof window !== 'undefined') {
    window.addEventListener('online', retryWhenOnline)
  }

  return () => {
    stopped = true
    clearTimeout(timer)
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', retryWhenOnline)
    }
  }
}
